<template>
  <div>
    <div
      class="slider-container mx-3 hideOnBigSizeSlider"
      :class="{ 'border-line-bottom': borderBottom }"
    >
      <div>
        <button
          v-if="filtering"
          type="button"
          class="arrow p-3 float-left"
          :class="{ disabled: !isLeftAvailable }"
          @click="left"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
      </div>

      <div class="center h-100 va titleByOne">
        <div v-if="currentItem" class="vam">
          <div v-if="currentItem.title" class="title h5 m-0">
            <span>{{ currentItem.title }}</span>
            <div v-if="currentItem.year" class="ml-2">
              {{ currentItem.year }}{{ yearSuffix }}
            </div>
          </div>
          <div v-if="currentItem.subtitle" class="subtitle h4 m-0">
            {{ currentItem.subtitle }}
          </div>
        </div>
      </div>

      <div>
        <button
          v-if="filtering"
          type="button"
          class="arrow p-3 float-right"
          :class="{ disabled: !isRightAvailable }"
          @click="right"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <div class="bigSize__week hideOnSmallSizeSlider">
      <div>
        <button
          v-if="filtering"
          type="button"
          class="arrow float-left"
          :class="{ disabled: !isLeftAvailable }"
          @click="left"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
      </div>

      <div ref="weekDayRef" class="week__day" :class="type">
        <button
          v-for="(rawItem, idx) in byTypeItems"
          :key="`slider-${idx}`"
          :ref="(el) => setWeekDayItemRef(el, idx)"
          type="button"
          class="day__week"
          :class="getClassByType(rawItem)"
          @click="setByDate(rawItem)"
        >
          <div v-if="displayItem(rawItem)" class="vam">
            <div class="title__week m-0">
              <span>{{ displayItem(rawItem).title }}</span>
            </div>
            <div class="subtitle__week m-0">
              <span v-if="type === 'year'">{{ displayItem(rawItem).year }}</span>
              <span>{{ displayItem(rawItem).subtitle }}</span>
            </div>
          </div>
        </button>
      </div>

      <div>
        <button
          v-if="filtering"
          type="button"
          class="arrow float-right"
          :class="{ disabled: !isRightAvailable }"
          @click="right"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [Object, String, Number, null],
    default: null,
  },
  borderBottom: {
    type: Boolean,
    default: false,
  },
  filtering: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String,
    default: "month",
  },
  month: {
    type: Array,
    default: () => [],
  },
  dontSelect: {
    type: Boolean,
    default: false,
  },
  yearSuffix: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const index = ref(0);
const weekDayRef = ref(null);
const weekDayItemRefs = ref([]);

const isLeftAvailable = computed(() => Boolean(props.items[index.value - 1]));
const isRightAvailable = computed(() => Boolean(props.items[index.value + 1]));
const currentItem = computed(() => {
  if (!props.items.length) return null;
  return props.items[index.value] || null;
});

const byTypeItems = computed(() => {
  if (props.type === "year") return props.items;

  if (props.type === "week") {
    return currentItem.value?.day?.week?.days || [];
  }

  const currentWeek = currentItem.value?.week;
  if (!currentWeek) return [];
  const monthItem = props.month.find((item) => item?.month === currentWeek?.month);
  return monthItem?.weeks || [];
});

const selectedByTypeIndex = computed(() => {
  if (!byTypeItems.value.length || !currentItem.value) return -1;

  if (props.type === "year") {
    const currentMonth = currentItem.value?.month;
    return byTypeItems.value.findIndex((item) => {
      if (typeof currentMonth === "object") {
        return (
          currentMonth?.month === item?.month?.month && currentMonth?.year === item?.year
        );
      }
      return currentMonth === item?.month;
    });
  }

  if (props.type === "week") {
    const currentTs = toDateTs(currentItem.value?.day?.date);
    return byTypeItems.value.findIndex((item) => toDateTs(item?.date) === currentTs);
  }

  const currentWeek = currentItem.value?.week;
  return byTypeItems.value.findIndex(
    (item) => item?.start === currentWeek?.start && item?.end === currentWeek?.end
  );
});

watch(
  () => props.items,
  (items) => {
    if (!Array.isArray(items) || !items.length) {
      index.value = 0;
      return;
    }

    if (!props.modelValue) {
      setByIndex(0);
      return;
    }

    const found = findModelIndex(props.modelValue);
    index.value = found >= 0 ? found : 0;
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  (value) => {
    if (!value || !props.items.length) return;
    const found = findModelIndex(value);
    if (found >= 0) index.value = found;
  }
);

watch(
  () => [byTypeItems.value.length, selectedByTypeIndex.value],
  () => {
    nextTick(() => scrollSelectedIntoView(true));
  },
  { immediate: true }
);

function findModelIndex(value) {
  if (!value) return -1;
  if (props.type === "week") {
    const ts = toDateTs(value?.day?.date);
    return props.items.findIndex((item) => toDateTs(item?.day?.date) === ts);
  }
  if (props.type === "month") {
    const start = value?.week?.start;
    const end = value?.week?.end;
    return props.items.findIndex(
      (item) => item?.week?.start === start && item?.week?.end === end
    );
  }
  return props.items.findIndex((item) => {
    if (item === value) return true;
    const im = typeof item?.month === "object" ? item.month?.month : item?.month;
    const vm = typeof value?.month === "object" ? value.month?.month : value?.month;
    const iy = typeof item?.month === "object" ? item.month?.year : item?.year;
    const vy = typeof value?.month === "object" ? value.month?.year : value?.year;
    return im === vm && iy === vy;
  });
}

function getClassByType(rawItem) {
  if (props.dontSelect || !currentItem.value) return "";

  if (props.type === "year") {
    const currentMonth = currentItem.value?.month;
    if (typeof currentMonth === "object") {
      return currentMonth?.month === rawItem?.month?.month ? "selected" : "";
    }
    return currentMonth === rawItem?.month ? "selected" : "";
  }

  if (props.type === "week") {
    return toDateTs(currentItem.value?.day?.date) === toDateTs(rawItem?.date)
      ? "selected"
      : "";
  }

  const currentWeek = currentItem.value?.week;
  return currentWeek?.start === rawItem?.start && currentWeek?.end === rawItem?.end
    ? "selected"
    : "";
}

function displayItem(rawItem) {
  if (props.type === "year") return rawItem;

  if (props.type === "week") {
    const ts = toDateTs(rawItem?.date);
    return props.items.find((item) => toDateTs(item?.day?.date) === ts) || null;
  }

  return (
    props.items.find(
      (item) => item?.week?.start === rawItem?.start && item?.week?.end === rawItem?.end
    ) || null
  );
}

function setByDate(rawItem) {
  if (props.type === "week") {
    const targetTs = toDateTs(rawItem?.date);
    const nextIndex = props.items.findIndex(
      (item) => toDateTs(item?.day?.date) === targetTs
    );
    setByIndex(nextIndex);
    return;
  }

  if (props.type === "month") {
    const nextIndex = props.items.findIndex(
      (item) => item?.week?.start === rawItem?.start && item?.week?.end === rawItem?.end
    );
    setByIndex(nextIndex);
    return;
  }

  const nextIndex = props.items.findIndex((item) => item === rawItem);
  setByIndex(nextIndex);
}

function left() {
  if (!isLeftAvailable.value) return;
  setByIndex(index.value - 1, "left");
}

function right() {
  if (!isRightAvailable.value) return;
  setByIndex(index.value + 1, "right");
}

function setByIndex(nextIndex, direction = null) {
  if (nextIndex < 0 || nextIndex >= props.items.length) return;

  index.value = nextIndex;
  const value = props.items[nextIndex];
  emit("update:modelValue", value);
  emit("change", value);

  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollSelectedIntoView(true, direction);
      });
    });
  });
}

function toDateTs(value) {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function setWeekDayItemRef(el, idx) {
  if (!el) return;
  weekDayItemRefs.value[idx] = el;
}

function scrollSelectedIntoView(smooth = false, direction = null) {
  const container = weekDayRef.value;
  const selectedIndex = selectedByTypeIndex.value;
  if (!container || selectedIndex < 0) return;

  const el = weekDayItemRefs.value[selectedIndex];
  if (!el) return;

  const containerLeft = container.scrollLeft;
  const containerRight = containerLeft + container.clientWidth;
  const itemLeft = el.offsetLeft;
  const itemRight = itemLeft + el.offsetWidth;
  const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
  const step = Math.max(20, Math.floor(container.clientWidth * 0.45));
  if (direction === "left" || direction === "right") {
    const delta = direction === "left" ? -step : step;
    const target = Math.max(0, Math.min(maxScroll, containerLeft + delta));

    container.scrollTo({
      left: target,
      behavior: smooth ? "smooth" : "auto",
    });
    return;
  }

  let nextLeft = containerLeft;
  if (itemLeft < containerLeft) {
    nextLeft = itemLeft;
  } else if (itemRight > containerRight) {
    nextLeft = itemRight - container.clientWidth;
  }

  container.scrollTo({
    left: Math.max(0, Math.min(maxScroll, nextLeft)),
    behavior: smooth ? "smooth" : "auto",
  });
}
</script>

<style scoped>
.month {
  justify-content: space-evenly;
}

.bigSize__week {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px 0 20px;
}

.bigSize__week .arrow {
  font-size: 24px;
}

.week__day {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 20px 0;
  overflow: auto;
  margin: 0 10px;
}

.selected {
  color: #8b8b8d !important;
  font-weight: 600;
}

.day__week {
  text-align: center;
  color: #bdbdbd;
  width: 140px !important;
  height: 43px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background: transparent;
  transition: color 0.2s ease, transform 0.2s ease;
}

.day__week.selected {
  transform: translateY(-1px);
}

.title__week {
  font-size: 18px;
  width: 140px !important;
}

.subtitle__week {
  font-size: 14px;
}

.slider-container {
  overflow: hidden;
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 20px;
  color: #8b8b8d;
}

.title span {
  text-align: center;
  width: 100%;
}
.titleByOne {
  display: flex;
  align-items: center;
}
.subtitle {
  color: #8b8b8d;
}

.arrow {
  color: #8b8b8d;
  font-size: 16px;
  border: none;
  background: transparent;
}

.arrow.disabled {
  color: #d9d9d9;
}

.center {
  width: auto;
  overflow: hidden;
  text-align: center;
  line-height: 1em;
}

::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}

::-webkit-scrollbar-thumb {
  background: #7b7b7c;
  border-radius: 0;
}

::-webkit-scrollbar-thumb:hover {
  background: #626262;
}

::-webkit-scrollbar-track {
  background: #fff;
  border-radius: 0;
  box-shadow: inset 7px 10px 12px #f0f0f0;
}
</style>
